// Simple Dashboard Component
import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const Dashboard = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();

  // Auto-redirect based on user role
  useEffect(() => {
    if (userProfile?.role) {
      if (userProfile.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (userProfile.role === 'manager') {
        navigate('/manager', { replace: true });
      } else if (userProfile.role === 'owner') {
        navigate('/owner', { replace: true });
      } else if (userProfile.role === 'guest') {
        // Redirect guest users to the new dashboard with persistent layout
        navigate('/guest/dashboard', { replace: true });
      }
    }
  }, [userProfile, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <h1> InnBotics</h1>
            <span>Hotel Management System</span>
          </div>
          <div className="user-actions">
            <div className="user-info">
              <span className="user-name">
                {userProfile?.displayName || currentUser?.email}
              </span>
              <span className="user-role">
                {userProfile?.role || 'Guest'}
              </span>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="welcome-section">
          <h2>Welcome to InnBotics Dashboard!</h2>
          <p>Your hotel management system is ready to use.</p>
        </div>

        <div className="user-details-card">
          <h3>👤 Your Account Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <label>Email:</label>
              <span>{currentUser?.email}</span>
            </div>
            <div className="detail-item">
              <label>Display Name:</label>
              <span>{userProfile?.displayName || 'Not set'}</span>
            </div>
            <div className="detail-item">
              <label>Role:</label>
              <span className={`role-badge ${userProfile?.role}`}>
                {userProfile?.role || 'guest'}
              </span>
            </div>
            <div className="detail-item">
              <label>Organization:</label>
              <span>{userProfile?.organization || 'Not specified'}</span>
            </div>
            <div className="detail-item">
              <label>User ID:</label>
              <span className="user-id">{currentUser?.uid}</span>
            </div>
            <div className="detail-item">
              <label>Email Verified:</label>
              <span className={`verification-status ${currentUser?.emailVerified}`}>
                {currentUser?.emailVerified ? 'Verified' : ' Not Verified'}
              </span>
            </div>
          </div>
        </div>

        {userProfile?.role === 'owner' && (
          <div className="admin-features">
            <h3>🏢 Hotel Owner Features</h3>
            <div className="feature-grid">
              <div className="feature-card" onClick={() => navigate('/owner')} style={{cursor: 'pointer'}}>
                <h4>📊 Owner Dashboard</h4>
                <p>View comprehensive property analytics and reports</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/owner/properties')} style={{cursor: 'pointer'}}>
                <h4>🏨 My Properties</h4>
                <p>Manage your hotel properties</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/owner/bookings')} style={{cursor: 'pointer'}}>
                <h4>📅 Bookings Overview</h4>
                <p>View all property bookings</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/owner/staff')} style={{cursor: 'pointer'}}>
                <h4>👥 Staff Management</h4>
                <p>Manage your hotel staff</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/owner/reports')} style={{cursor: 'pointer'}}>
                <h4>📈 Financial Reports</h4>
                <p>Revenue and performance analytics</p>
              </div>
            </div>
          </div>
        )}

        {userProfile?.role === 'admin' && (
          <div className="admin-features">
            <h3>🔧 Admin Features</h3>
            <div className="feature-grid">
              <div className="feature-card" onClick={() => navigate('/admin')} style={{cursor: 'pointer'}}>
                <h4>📊 Analytics Dashboard</h4>
                <p>View comprehensive analytics, graphs, and metrics</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/admin/hotel')} style={{cursor: 'pointer'}}>
                <h4>🏨 Hotel Management</h4>
                <p>Manage hotel properties and rooms</p>
              </div>
              <div className="feature-card"onClick={() => navigate('/admin/users')} style={{cursor: 'pointer'}}>
                <h4>� User Management</h4>
                <p>Manage staff and guest accounts</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/admin/notifications')} style={{cursor: 'pointer'}}>
                <h4>🔔 Notifications</h4>
                <p>Send notifications to users</p>
              </div>
              <div className="feature-card" onClick={() => navigate('/iot-management')} style={{cursor: 'pointer'}}>
                <h4>🏠 IoT Management</h4>
                <p>Configure and manage smart devices</p>
              </div>
            </div>
          </div>
        )}

        <div className="quick-actions">
          <h3>🚀 Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/room-controls')}>
              🏠 Room Controls
            </button>
            <button className="action-btn">
              📋 View Bookings
            </button>
            <button className="action-btn">
              🏠 Browse Rooms
            </button>
            <button className="action-btn">
              👤 Edit Profile
            </button>
            <button className="action-btn">
              📞 Contact Support
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;